import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  TableState,
  TableQueryParams,
  FetchDataFn,
  UseTableReturn,
  SortDirection,
  FilterState,
  PaginationState,
} from '../types/table';

/**
 * Custom hook for managing table state with server-side data fetching
 * 
 * Features:
 * - Server-side pagination
 * - Server-side sorting
 * - Server-side filtering
 * - Search functionality
 * - Row selection
 * - Automatic refetching
 * - Debouncing
 * - Loading states
 * - Error handling
 * 
 * @template TData - Type of the data object for each row
 */
export function useTable<TData = any>(
  fetchData: FetchDataFn<TData>,
  options: {
    initialPageSize?: number;
    refreshInterval?: number;
    searchDebounce?: number;
    onError?: (error: Error) => void;
  } = {}
): UseTableReturn<TData> {
  const {
    initialPageSize = 10,
    refreshInterval = 0,
    searchDebounce = 300,
    onError,
  } = options;

  // ============================================================================
  // State Management
  // ============================================================================

  const [data, setData] = useState<TData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [state, setState] = useState<TableState>({
    sort: null,
    filters: [],
    pagination: {
      page: 1,
      pageSize: initialPageSize,
      total: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false,
    },
    selectedRows: new Set(),
  });

  // Refs for managing side effects
  const abortControllerRef = useRef<AbortController | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  // Ensure isMountedRef is properly maintained
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Auto-retry on error with exponential backoff
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  // ============================================================================
  // Data Fetching
  // ============================================================================

  /**
   * Fetch data from server with current state
   */
  const fetchDataFromServer = useCallback(async () => {
    console.log('useTable: fetchDataFromServer called');
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      // Build query parameters
      const queryParams: TableQueryParams = {
        page: state.pagination.page,
        pageSize: state.pagination.pageSize,
        sortBy: state.sort?.columnId,
        sortDirection: state.sort?.direction || undefined,
        search: searchQuery || undefined,
        filters: state.filters.reduce((acc, filter) => {
          acc[filter.columnId] = {
            operator: filter.operator,
            value: filter.value,
          };
          return acc;
        }, {} as Record<string, any>),
      };

      // Fetch data
      const response = await fetchData(queryParams);

      // Only update if component is still mounted and request wasn't cancelled
      if (isMountedRef.current && !abortControllerRef.current.signal.aborted) {
        console.log('useTable: updating state with response:', response);
        setData(response.data);
        setState(prev => ({
          ...prev,
          pagination: {
            ...response.pagination,
            page: response.pagination.page,
            pageSize: response.pagination.pageSize,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages,
            hasNext: response.pagination.hasNext,
            hasPrev: response.pagination.hasPrev,
          },
        }));
        setLoading(false);
        setRetryCount(0); // Reset retry count on success
        console.log('useTable: state updated, loading set to false, retry count reset');
      } else {
        console.log('useTable: not updating state - mounted:', isMountedRef.current, 'aborted:', abortControllerRef.current.signal.aborted);
      }
    } catch (err) {
      console.log('useTable: caught error:', err);
      // Only handle error if component is still mounted and not aborted
      if (isMountedRef.current && !abortControllerRef.current?.signal.aborted) {
        console.log('useTable: setting error state, retry count:', retryCount);
        const error = err instanceof Error ? err : new Error('Failed to fetch data');
        
        // Auto-retry if we haven't exceeded max retries
        if (retryCount < maxRetries) {
          console.log('useTable: auto-retrying in 1 second...');
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            if (isMountedRef.current) {
              fetchDataFromServer();
            }
          }, 1000 * Math.pow(2, retryCount)); // Exponential backoff: 1s, 2s, 4s
        } else {
          console.log('useTable: max retries exceeded, showing error');
          setError(error);
          setLoading(false);
          onError?.(error);
        }
      } else {
        console.log('useTable: not setting error - mounted:', isMountedRef.current, 'aborted:', abortControllerRef.current?.signal.aborted);
      }
    }
  }, [fetchData, state.pagination.page, state.pagination.pageSize, state.sort, state.filters, searchQuery, onError]);

  // ============================================================================
  // Pagination Handlers
  // ============================================================================

  /**
   * Change current page
   */
  const setPage = useCallback((page: number) => {
    setState(prev => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        page,
      },
    }));
  }, []);

  /**
   * Change page size
   */
  const setPageSize = useCallback((pageSize: number) => {
    setState(prev => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        pageSize,
        page: 1, // Reset to first page when changing page size
      },
    }));
  }, []);

  // ============================================================================
  // Sorting Handlers
  // ============================================================================

  /**
   * Set sort for a column
   */
  const setSort = useCallback((columnId: string, direction: SortDirection) => {
    setState(prev => ({
      ...prev,
      sort: direction ? { columnId, direction } : null,
      pagination: {
        ...prev.pagination,
        page: 1, // Reset to first page when sorting
      },
    }));
  }, []);

  // ============================================================================
  // Filtering Handlers
  // ============================================================================

  /**
   * Add or update a filter
   */
  const setFilter = useCallback((filter: FilterState) => {
    setState(prev => {
      // Remove existing filter for this column
      const newFilters = prev.filters.filter(f => f.columnId !== filter.columnId);
      
      // Add new filter
      newFilters.push(filter);

      return {
        ...prev,
        filters: newFilters,
        pagination: {
          ...prev.pagination,
          page: 1, // Reset to first page when filtering
        },
      };
    });
  }, []);

  /**
   * Remove a filter
   */
  const removeFilter = useCallback((columnId: string) => {
    setState(prev => ({
      ...prev,
      filters: prev.filters.filter(f => f.columnId !== columnId),
      pagination: {
        ...prev.pagination,
        page: 1, // Reset to first page when removing filter
      },
    }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setState(prev => ({
      ...prev,
      filters: [],
      pagination: {
        ...prev.pagination,
        page: 1, // Reset to first page when clearing filters
      },
    }));
  }, []);

  // ============================================================================
  // Search Handlers
  // ============================================================================

  /**
   * Set search query with debouncing
   */
  const setSearch = useCallback((query: string) => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(query);
      setState(prev => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          page: 1, // Reset to first page when searching
        },
      }));
    }, searchDebounce);
  }, [searchDebounce]);

  // ============================================================================
  // Selection Handlers
  // ============================================================================

  /**
   * Toggle row selection
   */
  const toggleRowSelection = useCallback((rowId: string | number) => {
    setState(prev => {
      const newSelection = new Set(prev.selectedRows);
      if (newSelection.has(rowId)) {
        newSelection.delete(rowId);
      } else {
        newSelection.add(rowId);
      }
      return {
        ...prev,
        selectedRows: newSelection,
      };
    });
  }, []);

  /**
   * Select all rows on current page
   */
  const selectAllRows = useCallback(() => {
    setState(prev => {
      // This is a simplified version - in production, you might want to
      // handle selecting all rows across all pages differently
      const allIds = data.map((_, index) => index);
      return {
        ...prev,
        selectedRows: new Set(allIds),
      };
    });
  }, [data]);

  /**
   * Clear all selections
   */
  const clearSelection = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedRows: new Set(),
    }));
  }, []);

  // ============================================================================
  // Refresh Handler
  // ============================================================================

  /**
   * Manually refresh data
   */
  const refresh = useCallback(() => {
    fetchDataFromServer();
  }, [fetchDataFromServer]);

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Fetch data when dependencies change
   */
  useEffect(() => {
    fetchDataFromServer();
  }, [fetchDataFromServer]);

  /**
   * Setup auto-refresh interval
   */
  useEffect(() => {
    if (refreshInterval > 0) {
      refreshIntervalRef.current = setInterval(() => {
        fetchDataFromServer();
      }, refreshInterval);

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [refreshInterval, fetchDataFromServer]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Clear search timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Clear refresh interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  // ============================================================================
  // Return
  // ============================================================================

  return {
    data,
    state,
    loading,
    error,
    setPage,
    setPageSize,
    setSort,
    setFilter,
    removeFilter,
    clearFilters,
    setSearch,
    refresh,
    toggleRowSelection,
    selectAllRows,
    clearSelection,
  };
}

/**
 * Helper function to create pagination info
 */
export function createPaginationState(
  page: number,
  pageSize: number,
  total: number
): PaginationState {
  const totalPages = Math.ceil(total / pageSize);
  return {
    page,
    pageSize,
    total,
    totalPages,
    hasNext: page < totalPages,
    hasPrev: page > 1,
  };
}

